import { useState } from "react";
import PageShell from "@/components/PageShell";
import { useLang } from "@/context/LanguageContext";
import { useSettings } from "@/context/SettingsContext";
import { api, formatApiError } from "@/lib/api";
import { LocationSection, WhatsappCTA } from "@/pages/Home";
import { toast } from "sonner";
import { Mail, Phone, MapPin } from "lucide-react";

export default function Contact() {
    const { t } = useLang();
    const { settings } = useSettings();
    const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "", consent: false });
    const [submitting, setSubmitting] = useState(false);

    const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.consent) { toast.error("Bitte Zustimmung erteilen."); return; }
        setSubmitting(true);
        try {
            await api.post("/contact", form);
            toast.success(t.contact.form.success);
            setForm({ name: "", email: "", phone: "", subject: "", message: "", consent: false });
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally { setSubmitting(false); }
    };

    return (
        <PageShell>
            <section className="relative bg-brand-green text-white overflow-hidden">
                <div className="absolute inset-0 grainy opacity-50" />
                <div className="container-x relative py-20 md:py-24">
                    <div className="eyebrow text-brand-yellow">{t.contact.eyebrow}</div>
                    <h1 className="mt-4 font-display font-extrabold text-4xl md:text-6xl text-balance max-w-3xl">{t.contact.title}</h1>
                    <p className="mt-5 text-white/80 max-w-2xl text-lg">{t.contact.body}</p>
                </div>
            </section>

            <section className="container-x py-20 grid lg:grid-cols-12 gap-10">
                <div className="lg:col-span-5 space-y-4">
                    <div className="card-clean p-6 flex items-start gap-3"><MapPin className="h-5 w-5 text-brand-green mt-0.5" />
                        <div><div className="text-xs uppercase tracking-widest text-brand-charcoal/60">Adresse</div><div className="mt-1 text-sm">{settings.address}</div></div></div>
                    {settings.phone && <div className="card-clean p-6 flex items-start gap-3"><Phone className="h-5 w-5 text-brand-green mt-0.5" />
                        <div><div className="text-xs uppercase tracking-widest text-brand-charcoal/60">Telefon</div><a href={`tel:${settings.phone}`} className="mt-1 text-sm block">{settings.phone}</a></div></div>}
                    {settings.email && <div className="card-clean p-6 flex items-start gap-3"><Mail className="h-5 w-5 text-brand-green mt-0.5" />
                        <div><div className="text-xs uppercase tracking-widest text-brand-charcoal/60">E-Mail</div><a href={`mailto:${settings.email}`} className="mt-1 text-sm block">{settings.email}</a></div></div>}
                </div>

                <form className="lg:col-span-7 card-clean p-7 md:p-9" onSubmit={submit} data-testid="contact-form">
                    <div className="grid sm:grid-cols-2 gap-4">
                        <Field label={t.contact.form.name}><input required value={form.name} onChange={onChange("name")} className="input-clean" data-testid="contact-name" /></Field>
                        <Field label={t.contact.form.email}><input required type="email" value={form.email} onChange={onChange("email")} className="input-clean" data-testid="contact-email" /></Field>
                        <Field label={t.contact.form.phone}><input value={form.phone} onChange={onChange("phone")} className="input-clean" data-testid="contact-phone" /></Field>
                        <Field label={t.contact.form.subject}><input value={form.subject} onChange={onChange("subject")} className="input-clean" data-testid="contact-subject" /></Field>
                    </div>
                    <Field label={t.contact.form.message}>
                        <textarea required rows={5} value={form.message} onChange={onChange("message")} className="input-clean" data-testid="contact-message" />
                    </Field>
                    <label className="flex items-start gap-3 text-xs text-brand-charcoal/70">
                        <input type="checkbox" checked={form.consent} onChange={(e) => setForm((f) => ({ ...f, consent: e.target.checked }))} className="mt-0.5 h-4 w-4" data-testid="contact-consent" />
                        <span>{t.contact.form.consent}</span>
                    </label>
                    <button disabled={submitting} className="mt-6 btn-primary disabled:opacity-50" data-testid="contact-submit">
                        {submitting ? "..." : t.cta.send}
                    </button>
                </form>
            </section>

            <LocationSection />
            <WhatsappCTA />
        </PageShell>
    );
}

function Field({ label, children }) {
    return (
        <div className="mb-4 last:mb-0">
            <label className="block text-xs font-semibold uppercase tracking-widest text-brand-charcoal/60 mb-2">{label}</label>
            {children}
        </div>
    );
}
